'use strict';

const { getDependencyClass } = require('../../lib/dependencies.js');
const performanceMeasure = require('../../lib/performanceMeasure.js');
let measure = performanceMeasure('webcomponents:outbrain');
const { makeIntersectionObserver, getCurrentBreakpoint } = require('../../lib/util');

export const OUTBRAIN_VISIBLE = 'phoenix:outbrain:in-view';
const ATTR_NO_EMIT = 'no-emit';
const ATTR_BREAKPOINT = 'breakpoint';
const ATTR_NO_DEFER_LOAD = 'no-defer-load';

// Sane minimum -- 568 is iPhone 5-sized.
const MIN_VIEWPORT_HEIGHT = 568;
const IN_CONTENT_AD_THRESHOLD = 20;

let OUTBRAIN_SCRIPT_ADDED = false;
let resolveScriptLoaded;
const outbrainScriptLoaded = new Promise((resolve) => {
    resolveScriptLoaded = resolve;
});

getDependencyClass('phoenix-page').then((pageClass) => {
    /**
     * Outbrain
     *
     * This web component is responsible for providing injection points
     * for Outbrain to insert their widgets.
     *
     * If all conditions pass, it will add a `OUTBRAIN` class to a child element
     * and inject the Outbrain script. The script will then pick this element up
     * and replace its content with the widget that it is configured for.
     *
     * By default, this functionality is deferred until the user has scrolled
     * on the page, unless they arrived on the page that was already scrolled.
     *
     * By providing the `no-defer-load` attribute, the script will be injected
     * when the component is created, and won't wait for any scroll events.
     * The script will only be injected once across all `phoenix-outbrain`
     * components.
     *
     * If the `breakpoint` attribute is provided, then the `OUTBRAIN` class name
     * will only be added if the breakpoint on the client side matches this
     * attribute. This is to prevent Outbrain from injecting the widget
     * for every screen size, when it should only exist once.
     *
     * This component is no longer lazy-loaded in order
     * to minimize its CLS impact
     * See https://mavencorp.atlassian.net/browse/ADS-4621
     */
    class Outbrain extends pageClass.PhoenixElement {
        get loadOnScroll() {
            // ADS-6204 - suppressing this for now to improve Outbrain impressions
            // return !this.hasAttribute(ATTR_NO_DEFER_LOAD);
            return false;
        }

        get emitOnIntersect() {
            return !this.hasAttribute(ATTR_NO_EMIT);
        }

        get breakpoint() {
            return this.getAttribute(ATTR_BREAKPOINT);
        }

        connectedCallback() {
            Promise.all([
                this.canRender(),
                this._isAllowedBreakpoint(),
                pageClass.util.whenPageLoaded(),
            ]).then(([canRender, isAllowedBreakpoint]) => {
                // If blocked by canRender, or the breakpoint should be
                // hidden, short circuit the execution here
                if (!canRender || !isAllowedBreakpoint) {
                    this.setAttribute('aria-hidden', true);
                    this.style.display = 'none';
                    return;
                }

                // Sets up an intersection observer to emit an event when the
                // element is first visible within the viewport.
                if (this.emitOnIntersect) {
                    const observer = makeIntersectionObserver(
                        (entries) => {
                            if (entries[0].isIntersecting) {
                                observer.disconnect();
                                this.emitEvent(OUTBRAIN_VISIBLE, {
                                    bubbles: true,
                                    detail: {
                                        key: OUTBRAIN_VISIBLE,
                                    },
                                });
                            }
                        },
                        { root: document }
                    );
                    observer.observe(this);
                }

                if (this.isLoadDeferred()) {
                    const viewportHeight = Math.max(MIN_VIEWPORT_HEIGHT, window.innerHeight);

                    // Delays loading the Outbrain component until the user has scrolled
                    // within 1 viewport of the element. Once their script loads, it
                    // causes the root element to be a compositing layer, which results
                    // in memory issues, particularly for mobile devices. ADS-5985
                    this.observeFirstIntersection(`${viewportHeight}px 0px`);
                } else {
                    if (this.loadOnScroll) {
                        this._addListenerOrScript();
                    } else {
                        this.addOutbrainScript();
                    }
                }
                this._observeSizeChanges();
            });
        }

        _checkScrollPosition() {
            if (OUTBRAIN_SCRIPT_ADDED) {
                this._removeListener();
            } else {
                this.addOutbrainScript();
            }
        }

        _addListenerOrScript() {
            if (window.scrollY > 0) {
                this.addOutbrainScript();
            } else {
                this.scrollListener = pageClass.util.debounce(this._checkScrollPosition.bind(this), 50);
                window.addEventListener('scroll', this.scrollListener);
                this._observeSizeChanges();
            }
        }

        _removeListener() {
            if (this.scrollListener) {
                window.removeEventListener('scroll', this.scrollListener);
            }
        }

        isLoadDeferred() {
            /**
             * We want to limit the deferral of the Outbrain widget for certain devices that
             * exhibit memory issues once it loads. For now, this is limited to iPhone/iOS.
             * See: ADS-5985
             *
             * Representative iOS 16 user agent string:
             * Mozilla/5.0 (iPhone; CPU iPhone OS 16_7 like Mac OS X)
             *      AppleWebKit/605.1.15 (KHTML, like Gecko)
             *      Version/16.0 Mobile/15E148 Safari/604.1
             *
             * Representative iOS 17 user agent string:
             * Mozilla/5.0 (iPhone; CPU iPhone OS 17_0 like Mac OS X)
             *      AppleWebKit/605.1.15 (KHTML, like Gecko)
             *      Version/17.0 Mobile/15E148 Safari/604.1
             */

            try {
                // If there are more than 20 in-content ads, we should conditionally
                // defer the Outbrain resource for iOS devices. This will help mitigate
                // memory issues on these devices.
                const ff = window.phxBooleans?.['defer-outbrain'];
                if (ff) {
                    if (
                        parseInt(document.querySelector('.m-in-content-ad').dataset.zoneCount) >
                        IN_CONTENT_AD_THRESHOLD
                    ) {
                        return navigator.userAgent.includes('iPhone OS');
                    }
                }
            } catch (e) {
                // failed to determine in content ad count; let's just load Outbrain anyway
            }

            return false;
        }

        addOutbrainScript() {
            const obNode = this.querySelector('.is-waiting-to-load');

            // This allows outbrain script to pick this element up and inject the
            // widget into it. We only add it here, so that scripts are not
            // injected into hidden elements. We later call `researchWidget` so
            // that Outbrain rediscovers this placeholder
            outbrainScriptLoaded.then(() => {
                obNode.classList.add('OUTBRAIN');
                obNode.classList.remove('is-waiting-to-load');

                if (window.OBR) {
                    window.OBR.extern.researchWidget();
                }
            });

            if (OUTBRAIN_SCRIPT_ADDED) {
                return;
            }

            const id = 'ob_' + Math.floor(Math.random() * 1000);
            const src = 'https://widgets.outbrain.com/outbrain.js';
            OUTBRAIN_SCRIPT_ADDED = true;

            pageClass.util.addScriptResource(src, { id }, this).then(() => {
                resolveScriptLoaded();
            });

            window.OBREvents = window.OBREvents || [];
            window.OBREvents.push({
                name: 'rendered',
                widgetId: 'all',
                feedCards: false,
                callback(e) {
                    window.googleAnalyticsPromise.then(() => {
                        window.googleAnalytics.sendEvent(
                            'Outbrain', // category
                            'Outbrain_widget_load', // action
                            undefined, // label
                            undefined, // value,
                            { widgetId: e.widgetId, nonInteraction: true } // dimensions
                        );
                    });
                },
            });
        }

        onFirstIntersection() {
            this.addOutbrainScript();
        }

        _observeSizeChanges() {
            if (typeof ResizeObserver !== 'undefined') {
                let lastHeight = 0;
                //The purpose of this observer is to make some actions when element
                //outbrain-phoenix grows It is added because sometimes when the user
                //scroll is below this element and grows, produces massive layout shift
                this.resizeObserver = new ResizeObserver(() => {
                    const PERCENTAGE_IN_VIEWPORT_LIMIT = 60;
                    let scrollY = window.pageYOffset;
                    let top = this.getBoundingClientRect().top + scrollY;
                    let totalOffset = lastHeight + top;
                    //This condition will be true when outbrain-phoenix is partly seen in
                    //the viewport (less than PERCENTAGE_IN_VIEWPORT_LIMIT) and the scroll
                    //is below the start of the element
                    if (
                        this.clientHeight > 0 &&
                        scrollY < totalOffset &&
                        scrollY + window.innerHeight > totalOffset
                    ) {
                        const pixelsInViewport = totalOffset - scrollY;
                        const percentageInViewport = ((pixelsInViewport / window.innerHeight) * 100).toFixed(
                            3
                        );
                        if (percentageInViewport < PERCENTAGE_IN_VIEWPORT_LIMIT) {
                            //Automatically scroll to the position before element grows
                            let deltaY = this.clientHeight - lastHeight;
                            window.requestAnimationFrame(() => {
                                //Overwrite scrollBehaviour document style because if not
                                //scrollBy animation is smooth as default forced by _reset.scss
                                //We don't want the user to see this transition so we set it as auto
                                document.documentElement.style.scrollBehavior = 'auto';
                                window.scrollBy({
                                    top: deltaY,
                                    left: 0,
                                });
                                document.documentElement.style.scrollBehavior = '';
                            });
                        }
                    }
                    lastHeight = this.clientHeight;
                });
                this.resizeObserver.observe(this);
            }
        }

        /**
         * Returns `Promise<true>` if there is no `breakpoint` attribute set.
         * If the attribute exists, it makes sure it matches the `phxConfigQueue` breakpoint.
         * @returns {Promise<Boolean>}
         */
        _isAllowedBreakpoint() {
            if (!this.breakpoint) {
                return Promise.resolve(true);
            }

            return new Promise((resolve) => {
                const breakpoint = this.breakpoint;

                window.phxConfigQueue.push(function () {
                    resolve(this.breakpoint.get() === breakpoint);
                });
            });
        }

        disconnectedCallback() {
            if (this.resizeObserver) {
                this.resizeObserver.unobserve(this);
            }
            super.disconnectedCallback();
        }
    }

    window.customElements.define('phoenix-outbrain', Outbrain);
    measure.end();
});
